
import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { 
  LayoutDashboard, 
  CalendarDays, 
  Menu,
  Briefcase,
  Sparkles,
  Settings as SettingsIcon,
  Languages,
  Users,
  MessageCircle
} from 'lucide-react';
import { INITIAL_EVENTS, INITIAL_PAYMENTS, INITIAL_EXPENSES, INITIAL_KNOWLEDGE, INITIAL_LEADS } from './constants';
import { AppData, Event, Payment, Expense, EventStats, Lead, AIConfig, KnowledgeItem } from './types';
import { Dashboard } from './components/Dashboard';
import { EventList, EventDetail } from './components/EventModule';
import { OperationsModule } from './components/OperationsModule';
import { AiAssistant } from './components/AiModule';
import { Settings } from './components/Settings';
import { CRMModule } from './components/CRMModule';
import { ChatWidget } from './components/ChatWidget';
import { translations } from './translations';

const STORAGE_KEYS = {
  EVENTS: 'catermax_events',
  PAYMENTS: 'catermax_payments',
  EXPENSES: 'catermax_expenses',
  LEADS: 'catermax_leads',
  KNOWLEDGE: 'catermax_knowledge',
  AI_CONFIG: 'catermax_ai_config',
  LANG: 'catermax_lang',
  ACTIVE_SYSTEM: 'catermax_active_system'
};

const DEFAULT_AI_CONFIG: AIConfig = {
  activeProvider: 'groq',
  providers: {
    groq: { apiKey: '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', model: 'llama-3.3-70b-versatile', enabled: true },
    openai: { apiKey: '', model: 'gpt-4o', enabled: false },
    gemini: { apiKey: '', model: 'gemini-pro', enabled: false },
    openrouter: { apiKey: '', model: '', enabled: false }
  },
  systemCommands: `Role & Identity
You are CaterMax AI, the virtual sales assistant.
Your manner is polite, warm, and professional.

🏁 PRIMARY OBJECTIVE: CAPTURE THE LEAD FORM
Your main goal is to get the user to fill the Event Enquiry Form.
Do not simply annoy them with "fill this form".
Instead, CONVINCE them that the form is a tool to help them.

STRATEGY:
1. If they ask about Price/Quote:
   "Our pricing starts at ₹250/plate, but final cost depends on guest count and menu. I can generate an exact quote for you right now—just enter your details here." 
   -> [Trigger Form]

2. If they ask about Availability:
   "Let me check the calendar for that date. Please enter your event date here so I can confirm availability." 
   -> [Trigger Form]

3. If they are just browsing:
   "We have some great seasonal offers. If you have a tentative date, you can check if you qualify for a discount here." 
   -> [Trigger Form]

RULES:
- Do NOT ask for phone number in the chat text. The form will capture it.
- Do NOT negotiate prices in chat. Drive them to the form.
- Keep responses short (under 20 words where possible).
- Be helpful, not robotic.

Business Info:
- Catering for Weddings, Corporate, Parties.
- Loc: Bangalore.
- Price: Starts ₹250/plate.`,
  emailSettings: {
    enabled: true,
    email: 'catermaxindia@gmail.com',
    appPassword: 'zwbt hjhg iepq uqvs'
  },
  googleSheetsUrl: 'https://script.google.com/macros/s/AKfycbzUK0yh6IAzWBPkpDj_weWUreQBj0xBQy9eRiGoOVczYNLCPxxGS8th3fOwJ31ivF7j/exec'
};

const App: React.FC = () => {
  // --- EMBED DETECTION ---
  // We use a robust check that looks at the full URL to avoid parsing issues in some environments
  const [isEmbedMode, setIsEmbedMode] = useState(false);

  useEffect(() => {
    // Check for 'embed=true' in query string OR hash OR just present in URL
    const href = window.location.href;
    if (href.includes('embed=true')) {
        setIsEmbedMode(true);
        // Ensure body is transparent for the rounded iframe corners to look good
        document.body.style.backgroundColor = 'transparent';
    }
  }, []);

  // Initialize State from LocalStorage
  const [events, setEvents] = useState<Event[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.EVENTS);
    return saved ? JSON.parse(saved) : INITIAL_EVENTS;
  });

  const [payments, setPayments] = useState<Payment[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.PAYMENTS);
    return saved ? JSON.parse(saved) : INITIAL_PAYMENTS;
  });

  const [expenses, setExpenses] = useState<Expense[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.EXPENSES);
    return saved ? JSON.parse(saved) : INITIAL_EXPENSES;
  });

  const [leads, setLeads] = useState<Lead[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.LEADS);
    return saved ? JSON.parse(saved) : INITIAL_LEADS;
  });

  const [knowledgeBase, setKnowledgeBase] = useState<KnowledgeItem[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.KNOWLEDGE);
    return saved ? JSON.parse(saved) : INITIAL_KNOWLEDGE;
  });

  const [aiConfig, setAiConfig] = useState<AIConfig>(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.AI_CONFIG);
    // Merge saved config with default to ensure new fields like googleSheetsUrl are present if missing
    return saved ? { ...DEFAULT_AI_CONFIG, ...JSON.parse(saved) } : DEFAULT_AI_CONFIG;
  });

  const [language, setLanguage] = useState<'en' | 'kn'>(() => {
    return (localStorage.getItem(STORAGE_KEYS.LANG) as 'en' | 'kn') || 'en';
  });
  
  // System Switcher State (Finance vs CRM)
  const [activeSystem, setActiveSystem] = useState<'finance' | 'crm'>(() => {
    return (localStorage.getItem(STORAGE_KEYS.ACTIVE_SYSTEM) as 'finance' | 'crm') || 'finance';
  });

  // --- SYNC LISTENER (NEW) ---
  // This ensures that when the Chatbot (Iframe) updates LocalStorage, the Admin Dashboard updates instantly.
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      try {
        if (e.key === STORAGE_KEYS.EVENTS && e.newValue) setEvents(JSON.parse(e.newValue));
        if (e.key === STORAGE_KEYS.PAYMENTS && e.newValue) setPayments(JSON.parse(e.newValue));
        if (e.key === STORAGE_KEYS.EXPENSES && e.newValue) setExpenses(JSON.parse(e.newValue));
        if (e.key === STORAGE_KEYS.LEADS && e.newValue) setLeads(JSON.parse(e.newValue));
        if (e.key === STORAGE_KEYS.KNOWLEDGE && e.newValue) setKnowledgeBase(JSON.parse(e.newValue));
        if (e.key === STORAGE_KEYS.AI_CONFIG && e.newValue) setAiConfig(JSON.parse(e.newValue));
      } catch (err) {
        console.error("Error syncing storage change:", err);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [isChatWidgetOpen, setIsChatWidgetOpen] = useState(false);
  
  // Custom Navigation State
  const [currentPath, setCurrentPath] = useState('/');

  // Persistence Effects
  useEffect(() => { localStorage.setItem(STORAGE_KEYS.EVENTS, JSON.stringify(events)); }, [events]);
  useEffect(() => { localStorage.setItem(STORAGE_KEYS.PAYMENTS, JSON.stringify(payments)); }, [payments]);
  useEffect(() => { localStorage.setItem(STORAGE_KEYS.EXPENSES, JSON.stringify(expenses)); }, [expenses]);
  useEffect(() => { localStorage.setItem(STORAGE_KEYS.LEADS, JSON.stringify(leads)); }, [leads]);
  useEffect(() => { localStorage.setItem(STORAGE_KEYS.KNOWLEDGE, JSON.stringify(knowledgeBase)); }, [knowledgeBase]);
  useEffect(() => { localStorage.setItem(STORAGE_KEYS.AI_CONFIG, JSON.stringify(aiConfig)); }, [aiConfig]);
  useEffect(() => { localStorage.setItem(STORAGE_KEYS.LANG, language); }, [language]);
  useEffect(() => { localStorage.setItem(STORAGE_KEYS.ACTIVE_SYSTEM, activeSystem); }, [activeSystem]);

  // Helper: Calculate Stats for an Event
  const getEventStats = useCallback((eventId: string): EventStats => {
    const evt = events.find(e => e.id === eventId);
    const evtPayments = payments.filter(p => p.eventId === eventId);
    const evtExpenses = expenses.filter(e => e.eventId === eventId);

    const totalPaid = evtPayments.reduce((acc, p) => acc + p.amount, 0);
    const totalExpenses = evtExpenses.reduce((acc, e) => acc + e.amount, 0);
    const estimated = evt ? evt.finalTotal : 0;
    
    return {
      totalPaid,
      totalExpenses,
      balanceDue: Math.max(0, estimated - totalPaid),
      netProfit: totalPaid - totalExpenses,
      profitMargin: totalPaid > 0 ? ((totalPaid - totalExpenses) / totalPaid) * 100 : 0,
      costPerPlate: evt && evt.guestCount > 0 ? totalExpenses / evt.guestCount : 0
    };
  }, [events, payments, expenses]);

  // Context Object
  const dataContext: AppData = useMemo(() => ({
    events,
    payments,
    expenses,
    leads,
    knowledgeBase,
    aiConfig,
    addEvent: (e) => setEvents(prev => [...prev, e]),
    updateEvent: (updatedEvent) => setEvents(prev => prev.map(e => e.id === updatedEvent.id ? updatedEvent : e)),
    addPayment: (p) => setPayments(prev => [...prev, p]),
    addExpense: (e) => setExpenses(prev => [...prev, e]),
    deletePayment: (id) => setPayments(prev => prev.filter(p => p.id !== id)),
    deleteExpense: (id) => setExpenses(prev => prev.filter(e => e.id !== id)),
    deleteEvent: (id) => {
        setEvents(prev => prev.filter(e => e.id !== id));
        setPayments(prev => prev.filter(p => p.eventId !== id));
        setExpenses(prev => prev.filter(e => e.eventId !== id));
    },
    // CRM Actions
    addLead: (l) => setLeads(prev => [...prev, l]),
    updateLead: (l) => setLeads(prev => prev.map(lead => lead.id === l.id ? l : lead)),
    updateAIConfig: (c) => setAiConfig(c),
    addKnowledge: (k) => setKnowledgeBase(prev => [...prev, k]),
    deleteKnowledge: (id) => setKnowledgeBase(prev => prev.filter(k => k.id !== id)),

    getEventStats,
    resetAllData: () => {
      setEvents([]);
      setPayments([]);
      setExpenses([]);
      setLeads([]);
      setKnowledgeBase([]);
      localStorage.removeItem('groq_summary_cache');
      localStorage.removeItem('groq_anomalies_cache');
    },
    language,
    setLanguage,
    t: translations[language]
  }), [events, payments, expenses, leads, knowledgeBase, aiConfig, getEventStats, language]);

  // Toggle Logic
  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'kn' : 'en');
  };

  const activeEvent = selectedEventId ? events.find(e => e.id === selectedEventId) || null : null;
  const t = dataContext.t;

  // Custom Navigation Helper
  const navigate = (to: string) => {
    setCurrentPath(to);
    setIsSidebarOpen(false);
  };

  // --- EMBED MODE RENDERING ---
  // If ?embed=true is in URL, render ONLY the chatbot widget
  if (isEmbedMode) {
    return (
      <ChatWidget 
          data={dataContext} 
          isOpen={true} 
          onClose={() => {}} 
          isEmbed={true} 
      />
    );
  }

  // --- NORMAL APP MODE ---

  // NavLink Component
  const NavLink = ({ to, icon, label }: { to: string; icon: React.ReactNode; label: string }) => {
    const isActive = currentPath === to;
    return (
        <button
            onClick={() => navigate(to)}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors text-left ${
                isActive 
                ? 'bg-indigo-600 text-white' 
                : 'text-gray-600 hover:bg-gray-100'
            }`}
        >
            {icon}
            <span className="font-medium">{label}</span>
        </button>
    );
  };

  // Render Content based on currentPath
  const renderContent = () => {
    switch(currentPath) {
        case '/':
            return activeSystem === 'finance' ? <Dashboard data={dataContext} /> : <CRMModule data={dataContext} />;
        case '/events':
            return activeEvent 
                ? <EventDetail event={activeEvent} data={dataContext} onBack={() => setSelectedEventId(null)} />
                : <EventList data={dataContext} onSelectEvent={(e) => setSelectedEventId(e.id)} />;
        case '/operations':
            return <OperationsModule data={dataContext} />;
        case '/ai':
            return <AiAssistant data={dataContext} />;
        case '/settings':
            return <Settings data={dataContext} />;
        default:
            return <Dashboard data={dataContext} />;
    }
  };

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden relative">
        {/* Chat Widget Simulator (Internal) */}
        <ChatWidget 
            data={dataContext} 
            isOpen={isChatWidgetOpen} 
            onClose={() => setIsChatWidgetOpen(false)} 
        />
        
        {/* Floating Chat Button (Internal) */}
        {!isChatWidgetOpen && (
            <button 
                onClick={() => setIsChatWidgetOpen(true)}
                className="fixed bottom-6 right-6 z-50 bg-indigo-600 text-white p-4 rounded-full shadow-lg hover:bg-indigo-700 transition hover:scale-105"
                title="Simulate Website Customer"
            >
                <MessageCircle size={24} />
            </button>
        )}

        {/* Mobile Sidebar Overlay */}
        {isSidebarOpen && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-20 md:hidden"
            onClick={() => setIsSidebarOpen(false)}
          />
        )}

        {/* Sidebar */}
        <div className={`fixed inset-y-0 left-0 z-30 w-64 bg-white shadow-xl transform transition-transform duration-300 md:relative md:translate-x-0 ${
          isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}>
          <div className="flex flex-col h-full">
            <div className="p-6 border-b border-gray-100">
                <div className="flex items-center justify-between mb-4">
                    <h1 className="text-xl font-bold text-indigo-700 tracking-tight">CATERMAX</h1>
                    <button 
                    onClick={toggleLanguage}
                    className="p-1 rounded-full hover:bg-gray-100 text-indigo-600 transition border border-transparent hover:border-gray-200"
                    title="Switch Language"
                    >
                        <div className="flex items-center text-xs font-bold gap-1 px-1">
                            <Languages size={16} />
                            <span>{language === 'en' ? 'EN' : 'KN'}</span>
                        </div>
                    </button>
                </div>

                {/* System Switcher */}
                <div className="flex bg-gray-100 p-1 rounded-lg">
                    <button 
                        onClick={() => setActiveSystem('finance')}
                        className={`flex-1 py-2 text-xs font-bold rounded-md transition ${activeSystem === 'finance' ? 'bg-white text-indigo-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                        {t.sys_finance}
                    </button>
                    <button 
                        onClick={() => setActiveSystem('crm')}
                        className={`flex-1 py-2 text-xs font-bold rounded-md transition ${activeSystem === 'crm' ? 'bg-white text-indigo-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                        {t.sys_crm}
                    </button>
                </div>
            </div>

            <nav className="p-4 space-y-2 flex-1">
                {activeSystem === 'finance' ? (
                    <>
                        <NavLink to="/" icon={<LayoutDashboard size={20} />} label={t.nav_dashboard} />
                        <NavLink to="/events" icon={<CalendarDays size={20} />} label={t.nav_events} />
                        <NavLink to="/operations" icon={<Briefcase size={20} />} label={t.nav_ops} />
                        <NavLink to="/ai" icon={<Sparkles size={20} className="text-purple-600" />} label={t.nav_ai} />
                    </>
                ) : (
                    <>
                        <NavLink to="/" icon={<Users size={20} />} label={t.crm_title} />
                    </>
                )}
                
                <div className="pt-4 border-t border-gray-100 mt-4">
                   <NavLink to="/settings" icon={<SettingsIcon size={20} />} label={t.nav_settings} />
                </div>
            </nav>

            <div className="p-4 border-t border-gray-100">
                <div className="flex items-center gap-3 text-gray-500">
                <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold text-xs">CM</div>
                <div className="text-xs">
                    <p className="font-medium text-gray-700">{t.admin_user}</p>
                    <p>{t.owner}</p>
                </div>
                </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Top Bar for Mobile */}
          <header className="bg-white shadow-sm p-4 md:hidden flex items-center justify-between">
            <button onClick={() => setIsSidebarOpen(true)} className="text-gray-600">
              <Menu size={24} />
            </button>
            <div className="flex items-center gap-2">
                <span className="font-bold text-gray-800">CaterMax {activeSystem === 'crm' ? 'CRM' : ''}</span>
            </div>
            <div className="w-6"></div> 
          </header>

          <main className="flex-1 overflow-auto bg-gray-50">
            {renderContent()}
          </main>
        </div>
      </div>
  );
};

export default App;
